import Bugsnag from '@bugsnag/js'
import { Auth, Hub } from 'aws-amplify'
import { getAnalytics, setUserId, setUserProperties } from 'firebase/analytics'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import 'react-circular-progressbar/dist/styles.css'
import {
  useNotificationContext,
  usePartnerContext,
  useUserContext
} from '../context'
import { mutation } from '../graphql'
import { BUILD_ENV, notifyBugsnag } from '../helpers'
import { localStorage } from '../helpers/local-storage'
import { setNotification as setNotificationSingleton } from '../helpers/notification'
import { setPartner as setPartnerSingleton } from '../helpers/partner'
import { setUser as setUserSingleton } from '../helpers/user'
import { useEventListener } from '../hooks/use-event-listener'
import { useHubspotChat } from '../hooks/use-hubspot-chat'
import { useMount } from '../hooks/use-mount'

const StartupHandler = () => {
  const { t } = useTranslation()
  const isFirstRun = useRef(true)
  const { partner } = usePartnerContext()
  const { success, error } = useNotificationContext()
  const { activeSpace, refetchActiveSpace, cognitoUser } = useUserContext()

  const updateSpace = {
    mutate: async ({ input }, callbacks = {}) => {
      const { onSuccess } = callbacks

      const response = await mutation({ mutation: 'updateSpace', input })
      await refetchActiveSpace()

      onSuccess && onSuccess(response)
    }
  }

  useHubspotChat()

  useEventListener({
    event: 'focus',
    handler: () => {
      Auth.currentSession()
        .then(() =>
          updateSpace.mutate({
            input: {
              id: activeSpace.id,
              last_seen: new Date().toISOString()
            }
          })
        )
        .catch((err) => {
          if (err === 'No current user') {
            Hub.dispatch('auth', { event: 'signOut' })
            return
          }

          notifyBugsnag(err)
        })
    }
  })

  useEventListener({
    event: 'beforeunload',
    handler: () => {
      // reset filter and computed params
      localStorage.setItem('/careers', '')
      localStorage.setItem('/talents', '')
    }
  })

  /**
   * Handle App Opens localStorage
   */
  useEffect(() => {
    if (isFirstRun.current === false) return

    try {
      const opens_app = localStorage.getItem('opens_app')
      if (opens_app === null) {
        localStorage.setItem('opens_app', 1)
      } else {
        localStorage.setItem('opens_app', parseInt(opens_app) + 1)
      }
      isFirstRun.current = false
    } catch (error) {
      notifyBugsnag(error)
    }
  }, [])

  useMount(() => {
    setPartnerSingleton(partner)

    // Eigener "partner"-Tab auf allen Events → ergonomisch filterbar in
    // Bugsnag per partner.id / partner.name (statt vergraben im meta-Tab).
    Bugsnag.addMetadata('partner', { id: partner.id, name: partner.name })

    setNotificationSingleton({
      success,
      error,
      not_authorized_error_message: t('not_authorized_error_message')
    })

    const {
      create: can_create,
      read: can_read,
      update: can_update,
      delete: can_delete
    } = activeSpace.authorization_role.crud

    const email =
      activeSpace.person?.email || activeSpace.person.invitation_email

    setUserSingleton({
      ...activeSpace,
      email,
      username: cognitoUser.username,
      ...{ can_create, can_read, can_update, can_delete }
    })

    // Bugsnag-User setzen → schaltet "Users Affected" frei und ermöglicht
    // Filtern/Suchen nach User in der Triage.
    const { firstname, lastname, invitation_firstname, invitation_lastname } =
      activeSpace.person || {}
    const name =
      [firstname || invitation_firstname, lastname || invitation_lastname]
        .filter(Boolean)
        .join(' ') || undefined
    Bugsnag.setUser(cognitoUser.username, email, name)

    updateSpace.mutate({
      input: {
        id: activeSpace.id,
        last_seen: new Date().toISOString()
      }
    })

    if (BUILD_ENV !== 'production') return

    const analytics = getAnalytics()
    setUserId(analytics, partner.id)
    // lead_ampel (GRUEN | GELB | ROT, HubSpot-Sync) als User-Property →
    // GA4-Audiences/Segmente auf Grün-Kunden filterbar (Rock Q3/26)
    setUserProperties(analytics, {
      partner: partner.id,
      lead_ampel: partner.lead_ampel ?? ''
    })

    // Microsoft Clarity Custom Tags (Script in public/index.html, nur
    // production) → Session-Recordings nach Grün-Kunden/Partner filterbar.
    // clarity() puffert Calls vor Script-Load selbst (Stub-Queue).
    if (typeof window.clarity === 'function') {
      window.clarity('set', 'lead_ampel', partner.lead_ampel ?? 'UNSET')
      window.clarity('set', 'partner', partner.id)
    }
  })

  return null
}

export default StartupHandler
